import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useHasPermission } from "@/features/clients/hooks/use-permissions";
import { useClientLinks } from "@/features/clients/hooks/use-scoped-client";
import { useDossierFiles } from "@/features/dossiers/hooks/use-dossier-files";
import type { DossierFileSource } from "@/features/dossiers/types";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  ArrowRightIcon,
  FolderOpenIcon,
  PaperclipIcon,
} from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DropZone, useFileDrop } from "./drop-zone";
import { FileRow } from "./file-row";
import { useDossierFileActions } from "./use-file-actions";

/**
 * A contact's files — the first piece of the dossiers
 * (`backend/src/services/dossiers/docs/DOSSIERS.md`), in two shapes:
 *
 * - {@link DossierFiles}, the full module: the list and a drop zone, the whole
 *   area accepting a drop (the contact's "Fichiers" tab, a Messagerie request);
 * - {@link DossierFilesButton}, a button with the count that opens the list in a
 *   popover, for a header with no room for more (a qualification submission).
 *
 * Both hide themselves without `dossiers:view`, which is also what keeps them off
 * the platform back-office's view of a client (no membership there).
 * `source` is the screen the file is dropped from, recorded on the file.
 */
type DossierFilesProps = {
  customerId: string;
  source: DossierFileSource;
};

/** The hidden `<input type=file>` both shapes browse with. */
function useFilePicker(onFiles: (files: File[]) => void) {
  const ref = useRef<HTMLInputElement>(null);
  const input = (
    <input
      ref={ref}
      type="file"
      multiple
      className="hidden"
      onChange={(event) => {
        const files = Array.from(event.target.files || []);
        // Reset, so picking the same file again still fires onChange.
        event.target.value = "";
        if (files.length) onFiles(files);
      }}
    />
  );
  return { input, browse: () => ref.current?.click() };
}

export function DossierFiles({
  customerId,
  source,
  title,
  className,
}: DossierFilesProps & {
  /** A heading above the list; without it the module starts with the list. */
  title?: string;
  className?: string;
}) {
  const { t } = useTranslation("dossiers");
  const canView = useHasPermission("dossiers:view");
  const canUpload = useHasPermission("dossiers:upload");
  const canDelete = useHasPermission("dossiers:manage");

  const { data, isLoading, isError, refetch } = useDossierFiles(customerId, {
    enabled: canView,
  });
  const actions = useDossierFileActions(customerId, source);
  const picker = useFilePicker(actions.uploadFiles);
  const drop = useFileDrop(actions.uploadFiles, canUpload);

  if (!canView || !customerId) return null;
  const files = data?.files || [];

  return (
    <section
      {...drop.handlers}
      className={cn(
        "space-y-3 rounded-lg transition-colors",
        drop.dragging && "bg-primary/5 ring-2 ring-primary/40 ring-offset-4",
        className,
      )}
    >
      {title && (
        <h3 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          {title}
          {files.length > 0 && (
            <Badge variant="secondary" className="tabular-nums">
              {files.length}
            </Badge>
          )}
        </h3>
      )}

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      ) : isError ? (
        <ErrorState compact onRetry={refetch} />
      ) : files.length > 0 ? (
        <ul className="-mx-2 space-y-0.5">
          {files.map((file) => (
            <FileRow
              key={file.id}
              file={file}
              onOpen={() => actions.open(file)}
              onDownload={() => actions.download(file)}
              onRename={
                canUpload
                  ? (filename) => actions.rename(file, filename)
                  : undefined
              }
              onDelete={canDelete ? () => actions.remove(file) : undefined}
            />
          ))}
        </ul>
      ) : (
        !canUpload && (
          <EmptyState compact icon={FolderOpenIcon} title={t("empty.title")} />
        )
      )}

      {canUpload && (
        <>
          <DropZone
            onBrowse={picker.browse}
            dragging={drop.dragging}
            uploading={actions.uploading}
            compact={files.length > 0}
          />
          {picker.input}
        </>
      )}
    </section>
  );
}

export function DossierFilesButton({
  customerId,
  source,
  className,
}: DossierFilesProps & { className?: string }) {
  const { t } = useTranslation("dossiers");
  const canView = useHasPermission("dossiers:view");
  const canUpload = useHasPermission("dossiers:upload");
  const links = useClientLinks();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useDossierFiles(customerId, {
    enabled: canView,
  });
  const actions = useDossierFileActions(customerId, source);
  // Outside the popover: picking a file must land even if it has closed by then.
  const picker = useFilePicker(actions.uploadFiles);
  const drop = useFileDrop(actions.uploadFiles, canUpload);

  if (!canView || !customerId) return null;
  const files = data?.files || [];
  const contactHref = links.contact(customerId);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              drop.dragging && "border-primary bg-primary/5",
              className,
            )}
            aria-label={t("button.label")}
            {...drop.handlers}
          >
            <PaperclipIcon className="size-4" />
            <span className="hidden sm:inline">{t("button.label")}</span>
            {files.length > 0 && (
              <Badge
                variant="secondary"
                className="h-5 min-w-5 px-1.5 tabular-nums"
              >
                {files.length}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-80 space-y-2 p-2"
          {...drop.handlers}
        >
          <div className="flex items-center justify-between px-2 pt-1">
            <span className="text-sm font-medium">{t("button.title")}</span>
            {contactHref && (
              <Link
                to={`${contactHref}?tab=files`}
                onClick={() => setOpen(false)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                {t("button.seeAll")}
                <ArrowRightIcon className="size-3" />
              </Link>
            )}
          </div>
          {isLoading ? (
            <Skeleton className="h-11 w-full" />
          ) : files.length > 0 ? (
            <ul className="max-h-72 space-y-0.5 overflow-y-auto">
              {files.map((file) => (
                <FileRow
                  key={file.id}
                  file={file}
                  compact
                  onOpen={() => actions.open(file)}
                  onDownload={() => actions.download(file)}
                />
              ))}
            </ul>
          ) : (
            !canUpload && (
              <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                {t("empty.title")}
              </p>
            )
          )}
          {canUpload && (
            <DropZone
              compact
              onBrowse={picker.browse}
              dragging={drop.dragging}
              uploading={actions.uploading}
            />
          )}
        </PopoverContent>
      </Popover>
      {canUpload && picker.input}
    </>
  );
}
